import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { distinct, distinctBy, getTreatmentCodeByCountry, sortBySelectors } from "@lyfegen-platform/utils";
import { useFieldValue } from "formik";
import React, { useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { useScopeFields } from "../hooks/useScopeFields";
import { useScopes } from "../hooks/useScopes";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { FlexSectionSchema } from "../models/flexible/flex-section-schema";
import { Brand, PriceType, Treatment } from "../models/generated/graphql";
import { flexibleFormRelatedAgreements } from "../store/flexible";
import { getAttributeValuesDeep, getPreparationsByTreatmentSet } from "../utils/agreement-utils";
import { anyNamedEntity } from "../utils/any-named-entity";

export interface AdHocSectionValues {
	[AttributeIdentifiers.BrandNameReference]: Brand;
	[AttributeIdentifiers.PackReference]: Treatment;
	[AttributeIdentifiers.BasePrice]: PriceType;
	[AttributeIdentifiers.SuggestedDiscountRate]: number;
	[AttributeIdentifiers.FixedFeeAmount]: number;
}

const BrandsFillingFromFormEffect = (props: { section: FlexSectionController }) => {
	const [values, setValues] = useFieldValue(props.section.path);
	const relatedAgreementsState = useRecoilValue(flexibleFormRelatedAgreements);
	const { selectedScopes } = useScopes(props.section.context);
	const { multiBrandSets, generics } = useScopeFields(props.section.context);
	const existingPacks = props.section.context.isEditable ? [] : getAttributeValuesDeep(values, AttributeIdentifiers.PackReference);
	const treatmentWithPrices = useMemo(() => {
		const scenarioBrands = relatedAgreementsState.data?.flatMap((agreementData) =>
			agreementData.agreement.scenarios.flatMap((scenario) =>
				scenario.actions.flatMap((action) => action.brandsConnection.edges.map((edge) => edge.node.Name))
			)
		);

		return distinct(
			distinctBy(
				relatedAgreementsState.data?.map((agreementData) => agreementData.agreement) || [],
				(agreement) => agreement.id
			).flatMap((agreement) =>
				agreement.treatmentPricesConnection.edges
					.filter((treatment) => !scenarioBrands?.length || scenarioBrands.includes(treatment.node.regulatedBy.BrandName))
					.flatMap((treatment) => getTreatmentCodeByCountry(treatment.node))
					.filter(Boolean)
			)
		) as Array<string>;
	}, [relatedAgreementsState.data]);

	const getUniqueBrandIndexes = (items: Array<AdHocSectionValues>) => {
		const result: Array<number> = [];
		const childIndexes: {
			[index: number]: Array<number>;
		} = {};

		for (let index = 0; index < items.length; index++) {
			if (
				(!!items[index - 1] && items[index - 1].brandNameReference.Name !== items[index].brandNameReference.Name) ||
				!items[index - 1]
			) {
				result.push(index);
			} else {
				const childIndex = childIndexes[result[result.length - 1]];
				if (childIndex) {
					childIndexes[result[result.length - 1]].push(index);
				} else {
					childIndexes[result[result.length - 1]] = [index];
				}
			}
		}

		return {
			uniqueBrandIndexes: result,
			childBrandIndexes: childIndexes,
		};
	};

	useEffect(() => {
		if (relatedAgreementsState.loading || !relatedAgreementsState.data || props.section.context.isNegotiable) {
			return;
		}

		const addedTreatments: Array<string | undefined | null> = [];
		const items = multiBrandSets.flatMap((brandSet, index) =>
			selectedScopes
				.flatMap((scope) => scope.treatmentSets)
				.filter((treatmentSet) => treatmentSet.generic.Name === generics[index]?.Name)
				.flatMap((treatmentSet) =>
					treatmentSet.treatments.length
						? treatmentSet.treatments
						: getPreparationsByTreatmentSet(treatmentSet).flatMap((preparation) => preparation.regulates)
				)
				.filter((treatment) => {
					return (
						treatmentWithPrices.includes(getTreatmentCodeByCountry(treatment) || "") &&
						(!brandSet.length ||
							brandSet.some((brand) => brand.Name === anyNamedEntity.Name || brand.Name === treatment.regulatedBy.BrandName))
					);
				})
				.concat(existingPacks)
				.map((treatment) => {
					if (addedTreatments.includes(getTreatmentCodeByCountry(treatment) || "")) {
						return null;
					}

					const sourceRow = values.find(
						({ brandNameReference, packReference }: AdHocSectionValues) =>
							brandNameReference.Name === treatment.regulatedBy.BrandName && treatment.Code === packReference.Code
					);
					addedTreatments.push(getTreatmentCodeByCountry(treatment) || "");

					return {
						[AttributeIdentifiers.BrandNameReference]: treatment.regulatedBy.brand,
						[AttributeIdentifiers.PackReference]: treatment,
						[AttributeIdentifiers.BasePrice]: sourceRow ? sourceRow.basePrice : null,
						[AttributeIdentifiers.SuggestedDiscountRate]: sourceRow ? sourceRow.suggestedDiscountRate : "",
						[AttributeIdentifiers.FixedFeeAmount]: sourceRow ? sourceRow.fixedFeeAmount : "",
					};
				})
				.filter(Boolean)
		);
		const uniqueSortedItems = distinctBy(
			items,
			(item) =>
				`${(item![AttributeIdentifiers.BrandNameReference] as Brand).Name}.${
					(item![AttributeIdentifiers.PackReference] as Treatment).Code
				}`
		).sort(
			sortBySelectors(
				(item) => (item![AttributeIdentifiers.BrandNameReference] as Brand).Name,
				(item) => (item![AttributeIdentifiers.PackReference] as Treatment).Code
			)
		);
		setValues(uniqueSortedItems);
		const { childBrandIndexes, uniqueBrandIndexes } = getUniqueBrandIndexes(uniqueSortedItems as Array<AdHocSectionValues>);
		props.section.context.uniqueBrandIndexes = uniqueBrandIndexes;
		props.section.context.childBrandIndexes = childBrandIndexes;
	}, [
		multiBrandSets
			.flatMap((brandSet) => brandSet.flatMap((brand) => brand.Name))
			.concat(generics.map((generic) => generic.Name))
			.concat(selectedScopes.map((scope) => scope.scopeId))
			.concat(treatmentWithPrices)
			.join(","),
		relatedAgreementsState.loading,
	]);

	useEffect(() => {
		if (!props.section.context.isNegotiable) {
			return;
		}

		const { childBrandIndexes, uniqueBrandIndexes } = getUniqueBrandIndexes(values as Array<AdHocSectionValues>);
		props.section.context.uniqueBrandIndexes = uniqueBrandIndexes;
		props.section.context.childBrandIndexes = childBrandIndexes;
	}, [relatedAgreementsState.loading, relatedAgreementsState.data]);

	return null;
};

export class AdHocSectionController implements FlexSectionController {
	context: FlexContext = {} as any;

	constructor(
		readonly schema: FlexSectionSchema,
		readonly path: string,
		readonly fieldControllers: Array<FlexFieldController>,
		readonly sectionControllers: Array<FlexSectionController>
	) {}

	useCanDeleteItem(): boolean {
		return false;
	}

	useCanAddItem(): boolean {
		return false;
	}

	renderBeforeHeader(): React.ReactElement {
		return <BrandsFillingFromFormEffect section={this} />;
	}
}
